import React, { useEffect, useState } from 'react';
import { Wallet, BadgePlus } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { getUserWallet } from '../../lib/api';
import DepositModal from './DepositModal';
import HistoryModal from './HistoryModal';
import { formatCompact } from '../../lib/format';

/**
 * Compact wallet badge always visible in the header when user is logged in.
 * Shows live available balance and a Deposit button.
 * Hidden entirely when the tenant has disabled the user wallet.
 */
export default function WalletPill({ inDark = false }) {
  const { user } = useAuth();
  const { walletUserEnabled, depositEnabled } = useSite();
  const [balance, setBalance] = useState(null);
  const [depositOpen, setDepositOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const reload = async () => {
    try { const d = await getUserWallet(); setBalance(d.balance); }
    catch (e) { /* */ }
  };

  useEffect(() => {
    if (!user) return;
    reload();
    // Faster live update: poll every 6s so admin approvals appear quickly
    const t = setInterval(reload, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user?.id]);

  if (!user || !walletUserEnabled) return null;

  return (
    <>
      <div className="flex items-center gap-1 md:gap-1.5" data-testid="wallet-pill">
        <button
          onClick={() => setHistoryOpen(true)}
          className={`group relative flex items-center gap-1 h-7 md:h-8 px-2 md:px-2.5 rounded-full font-bold border transition wallet-pill-glow ${inDark ? 'bg-black/85 border-black/40 hover:bg-zinc-900' : 'bg-black border-black/40 hover:bg-zinc-800'} text-[color:var(--gold)]`}
          aria-label="Wallet"
        >
          <Wallet size={12} />
          <span className="text-[10px] md:text-xs tabular-nums notranslate" translate="no" data-testid="wallet-pill-balance">
            ₹{balance == null ? '—' : formatCompact(balance)}
          </span>
        </button>
        <button
          onClick={() => setDepositOpen(true)}
          className={`relative flex items-center gap-1 h-7 md:h-8 px-2 md:px-3 rounded-full bg-[color:var(--gold)] hover:brightness-110 text-black font-bold text-[10px] md:text-xs transition shadow shadow-amber-500/30 deposit-shimmer overflow-hidden ${depositEnabled ? '' : 'hidden'}`}
          aria-label="Deposit"
          data-testid="wallet-pill-deposit"
        >
          <BadgePlus size={13} strokeWidth={2.5} />
          <span className="notranslate relative z-10" translate="no">Deposit</span>
        </button>
      </div>
      <DepositModal open={depositOpen} onOpenChange={setDepositOpen} onSubmitted={reload} />
      <HistoryModal open={historyOpen} onOpenChange={setHistoryOpen} />
    </>
  );
}
